import ListFilters from "@/common/components/ListFilters";
import Table from "@/common/components/Table";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { anpPricesByGeoTypeColumns } from "../constants/anpPricesDefaultColumns.constant";
import { useAnpPrices } from "../hooks/useAnpHistoricalPrices";
import { typeStrategy } from "@/common/constants/typeStrategy.constant";
import { useProducts } from "@/common/hooks/queries/useProducts";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { ProductData } from "@/common/types/product.type";
import { AnpHistoricalPricesTransformer } from "../services/anpHistoricalPricesTransformer";
import { useGeoOptionsByType } from "@/common/hooks/queries/useGeoOptionsByType";
import { geosMap } from "@/common/constants/geosMap.constant";
import { GeoTransformers } from "@/common/services/geoTransformers";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";

interface AnpHistoricalPricesTableProps {
  reportId?: string;
  geoType: keyof typeof typeStrategy;
}

const AnpHistoricalPricesTable = ({ reportId, geoType }: AnpHistoricalPricesTableProps) => {
  const { filterParams, setFilterParams } = useFilterParams();

  const geoTypeValue = geosMap.find((geo) => geo.type === geoType)?.value!;

  const [searchGeo, setSearchGeo] = useDebounce('');
  const [searchProduct, setSearchProduct] = useDebounce('');

  const { data: productOptions } = useProducts({
    filters: { search: searchProduct },
    mapper: Mapper.mapToOptions<ProductData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  });

  const { data: geoOptions } = useGeoOptionsByType({
    filters: {
      type: geoTypeValue,
      search: searchGeo,
    },
    mapper: Mapper.mapWithTransformer({ transformer: GeoTransformers.optionsTransformer() }),
  });

  const { data: anpPrices, meta, isFetching: anpPricesIsFetching, error: anpPricesError = {} as any, refetch: refetchAnpPrices } = useAnpPrices({
    filters: {
      report_id: reportId,
      product_id: filterParams.product?.value,
      geo_type: geoType,
      geo_city_id: geoType === 'Geo::City' ? filterParams.cities?.value : '',
      geo_state_id: geoType === 'Geo::State' ? filterParams.states?.value : '',
      geo_region_id: geoType === 'Geo::Region' ? filterParams.regions?.value : '',
      page: filterParams.page || 1,
   },
    mapper: Mapper.mapWithTransformer({ transformer: AnpHistoricalPricesTransformer.tableTransformer(geoType) }),
  });

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-340px)]"
      statusCode={anpPricesError?.status}
      refetchFunction={refetchAnpPrices}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <div className="flex gap-2 items-center">
            <ListFilters
              filters={[
                {
                  className: 'min-w-40',
                  placeholder: 'Produto',
                  options: [{ label: 'Todos', value: '' } , ...productOptions],
                  onChange: (product) => setFilterParams((prev) => ({ ...prev, product })),
                  onInputChange: setSearchProduct,
                  value: filterParams.product,
                },
                {
                  className: geoType === 'Geo::Country' ? 'hidden' : 'min-w-40',
                  placeholder: typeStrategy[geoType],
                  options: [{ label: 'Todos', value: '' } , ...geoOptions],
                  onChange: (option) => setFilterParams((prev) => ({ ...prev, [geoTypeValue]: option })),
                  onInputChange: setSearchGeo,
                  value: filterParams[geoTypeValue],
                },
              ]}
            />
          </div>
        </div>

        <div>
          <Table
            className="max-h-[calc(100vh-276px-7.5rem)]"
            columns={anpPricesByGeoTypeColumns[geoType]}
            rows={anpPrices}
            isLoadingData={anpPricesIsFetching}
            hasPagination
            rowEmptyValue="--"
            totalCount={meta.total}
            totalPages={meta.total_pages}
            page={filterParams.page ? Number(filterParams.page) : 1}
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          />
        </div>
      </div>
    </ErrorMessageWrapper>
  );
};

export default AnpHistoricalPricesTable;
